import type {
  QualifyBudgetList,
  QualifyBudgetResponse,
  QualifyBudgetView,
} from '@platform/contracts';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Switch,
} from '@platform/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gauge } from 'lucide-react';
import { type SubmitEvent, useId, useState } from 'react';
import { toast } from 'sonner';

import { api, errorMessage } from '../lib/api';
import { fmtDateTime, useScopedQuery } from '../lib/console';
import { fmtAgo } from '../lib/format';
import { Field, FormError, fd } from './form';
import { Loadable } from './states';

/**
 * Calificación según demanda (WF02 sólo gasta Gemini cuando la campaña necesita stock):
 *  - `QualifyBudgetTab`: ajustes de una empresa (Integraciones → Calificación).
 *  - `QualifyBudgetCard`: estado de todas las empresas del alcance (Estado del sistema).
 */

const fmtNum = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : n.toLocaleString('es-ES');

const REASON_LABEL: Record<QualifyBudgetResponse['reason'], string> = {
  disabled: 'apagada',
  stock_ok: 'no califica: stock suficiente',
  in_flight: 'no reclama más: ya hay leads calificándose',
  deficit: 'califica',
  no_data: 'sin datos: califica con el lote de siempre',
};

const REASON_VARIANT = {
  disabled: 'secondary',
  stock_ok: 'success',
  in_flight: 'secondary',
  deficit: 'warning',
  no_data: 'destructive',
} as const;

/** «Calificación según demanda: activa · stock X de Y · hoy se calificaron N». */
export function qualifyBudgetSummary(v: QualifyBudgetView): string {
  if (!v.settings.enabled) return 'Calificación según demanda: apagada';
  const target = v.settings.targetPerDay * v.settings.bufferDays;
  const last = v.last;
  const stock = last?.stock ?? null;
  const today = last?.qualifiedToday ?? null;
  return [
    'Calificación según demanda: activa',
    `stock ${fmtNum(stock)} de ${fmtNum(target)}`,
    `hoy se calificaron ${fmtNum(today)}`,
  ].join(' · ');
}

// ------------------------------------------------------------------ Config (una empresa)

export function QualifyBudgetTab({ tenantId, canWrite }: { tenantId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const switchId = useId();
  const [error, setError] = useState<string | null>(null);
  const url = `/api/tenants/${tenantId}/qualify-budget`;
  const q = useQuery({
    queryKey: ['qualify-budget', tenantId],
    queryFn: () => api<QualifyBudgetView>(url),
  });
  const save = useMutation({
    mutationFn: (body: { enabled: boolean; targetPerDay: number; bufferDays: number }) =>
      api<QualifyBudgetView>(url, { method: 'PUT', body }),
    onSuccess: (r) => {
      qc.setQueryData(['qualify-budget', tenantId], r);
      void qc.invalidateQueries({ queryKey: ['console'] });
      toast.success('Guardado');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const v = q.data;
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const on = enabled ?? v?.settings.enabled ?? false;

  function onSubmit(e: SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const targetPerDay = Number(fd(f, 'targetPerDay'));
    const bufferDays = Number(fd(f, 'bufferDays'));
    if (!Number.isInteger(targetPerDay) || targetPerDay < 1 || targetPerDay > 1000) {
      setError('Envíos objetivo por día: un número entero entre 1 y 1000.');
      return;
    }
    if (!Number.isInteger(bufferDays) || bufferDays < 1 || bufferDays > 20) {
      setError('Días de colchón: un número entero entre 1 y 20.');
      return;
    }
    setError(null);
    save.mutate({ enabled: on, targetPerDay, bufferDays });
  }

  return (
    <Loadable isLoading={q.isLoading} error={q.error}>
      {v ? (
        <div className="flex flex-col gap-4">
          <Callout tone="info">
            Con esto encendido, la calificación (Gemini) sólo trabaja cuando la campaña necesita
            leads: si los leads listos para enviar (en Instantly sin contactar + en cola) cubren los
            envíos de los próximos días de colchón, esa vuelta no califica y los leads esperan. Los
            días son días de envío (lunes a viernes). Si la plataforma no responde, se califica como
            siempre.
          </Callout>
          <Card>
            <CardContent className="p-4">
              <form className="flex flex-col gap-4" onSubmit={onSubmit} key={v.updatedAt ?? 'new'}>
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <Label htmlFor={switchId}>Calificación según demanda</Label>
                    <p className="text-sm text-muted-foreground">
                      {on ? 'Encendida' : 'Apagada: se califica todo lo que entra.'}
                    </p>
                  </div>
                  <Switch
                    id={switchId}
                    checked={on}
                    disabled={!canWrite || save.isPending}
                    onCheckedChange={setEnabled}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Envíos objetivo por día"
                    name="targetPerDay"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={1000}
                    defaultValue={v.settings.targetPerDay}
                    disabled={!canWrite}
                    hint="Cuántos emails por día de envío tiene que poder mandar la campaña."
                  />
                  <Field
                    label="Días de colchón"
                    name="bufferDays"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={20}
                    defaultValue={v.settings.bufferDays}
                    disabled={!canWrite}
                    hint="Días de envío que tiene que cubrir el stock antes de dejar de calificar."
                  />
                </div>
                <FormError message={error} />
                {canWrite ? (
                  <div>
                    <Button type="submit" disabled={save.isPending}>
                      Guardar
                    </Button>
                  </div>
                ) : null}
              </form>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex flex-col gap-1 p-4 text-sm">
              <p className="font-medium">{qualifyBudgetSummary(v)}</p>
              {v.last ? (
                <>
                  <p className="text-muted-foreground">{v.last.message}</p>
                  <p className="text-xs text-muted-foreground" title={fmtDateTime(v.lastDecidedAt)}>
                    Última consulta de WF02 {fmtAgo(v.lastDecidedAt)} · hoy: {v.today.runsAllowed}{' '}
                    vueltas calificaron, {v.today.runsSkipped} no
                    {v.today.runsFailOpen ? `, ${String(v.today.runsFailOpen)} sin datos` : ''}
                  </p>
                </>
              ) : (
                <p className="text-muted-foreground">WF02 todavía no consultó a la plataforma.</p>
              )}
              {v.updatedByEmail ? (
                <p className="text-xs text-muted-foreground">
                  Último cambio: {v.updatedByEmail} · {fmtDateTime(v.updatedAt)}
                </p>
              ) : null}
            </CardContent>
          </Card>
        </div>
      ) : null}
    </Loadable>
  );
}

// ------------------------------------------------------------------ Estado del sistema

export function QualifyBudgetCard() {
  const q = useScopedQuery<QualifyBudgetList>(['qualify-budget'], '/api/ops/qualify-budget', {
    requires: 'ops:read',
  });
  const items = q.data?.items ?? [];
  if (!q.data || items.length === 0) return null;
  return (
    <Card className="mt-6 overflow-hidden">
      <CardHeader>
        <CardTitle icon={<Gauge />}>Calificación según demanda</CardTitle>
        <CardDescription>
          Gemini sólo califica cuando la campaña necesita leads. Se configura en Integraciones →
          Calificación.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {items.map((v) => (
          <div
            key={v.tenantId}
            className="flex flex-col gap-0.5 border-b pb-3 last:border-0 last:pb-0"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{v.tenantName}</span>
              {v.settings.enabled && v.last ? (
                <Badge variant={REASON_VARIANT[v.last.reason]}>{REASON_LABEL[v.last.reason]}</Badge>
              ) : (
                <Badge variant="secondary">
                  {v.settings.enabled ? 'sin consultas todavía' : 'apagada'}
                </Badge>
              )}
            </div>
            <p className="text-sm">{qualifyBudgetSummary(v)}</p>
            {v.settings.enabled && v.last ? (
              <p className="text-xs text-muted-foreground" title={fmtDateTime(v.lastDecidedAt)}>
                {v.last.message} · {fmtAgo(v.lastDecidedAt)} · hoy {v.today.runsAllowed} vueltas
                calificaron y {v.today.runsSkipped} no
              </p>
            ) : null}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
